import React from "react";
import { Search, Sparkles, MessageCircle, CalendarCheck } from "lucide-react";

const screens = [
  {
    label: "Discover",
    icon: Search,
    accent: "Browse fit profiles by goal, split & gym",
  },
  {
    label: "Match",
    icon: Sparkles,
    accent: "Mutual goals = instant chemistry",
  },
  {
    label: "Chat",
    icon: MessageCircle,
    accent: "Plan a session, send a PR, vibe",
  },
  {
    label: "Schedule",
    icon: CalendarCheck,
    accent: "Lock in a workout date — together",
  },
];

const PhoneMock = ({ index, label, Icon, accent }) => {
  // Simulated phone UI with on-brand chrome
  return (
    <div
      data-testid={`phone-mock-${label.toLowerCase()}`}
      className="relative w-[230px] sm:w-[260px] flex-shrink-0 group"
      style={{ transform: `rotate(${index % 2 === 0 ? -2 : 2}deg)` }}
    >
      <div className="relative aspect-[9/19] rounded-[2.2rem] bg-[#0A0A0A] border border-white/10 p-2 shadow-[0_30px_80px_-20px_rgba(198,255,0,0.25)] transition-transform duration-500 group-hover:-translate-y-2 group-hover:rotate-0">
        {/* Notch */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 rounded-b-2xl bg-black z-20" />

        <div className="relative h-full w-full rounded-[1.7rem] overflow-hidden bg-gradient-to-b from-[#161616] to-[#0A0A0A] flex flex-col">
          {/* Status bar */}
          <div className="px-5 pt-4 pb-2 flex items-center justify-between text-[9px] text-neutral-400 uppercase tracking-widest">
            <span>9:41</span>
            <span className="font-heading text-white text-sm">{label}</span>
            <span className="text-[#C6FF00]">●</span>
          </div>

          {/* Body */}
          <div className="px-4 pb-4 pt-2 flex-1 flex flex-col gap-3">
            {label === "Discover" && (
              <div className="flex-1 rounded-2xl bg-gradient-to-br from-neutral-800 to-neutral-900 border border-white/5 relative overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1572459815549-873917ec8c0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxmaXQlMjBibGFjayUyMGNvdXBsZSUyMGd5bXxlbnwwfHx8fDE3NzcxNTg5OTF8MA&ixlib=rb-4.1.0&q=85"
                  alt="profile"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="font-heading text-white text-lg leading-tight">JADA, 27</div>
                  <div className="text-[10px] text-neutral-300 mt-1">Powerlifting · 4x/wk</div>
                  <div className="mt-2 inline-flex px-2 py-0.5 rounded-full bg-[#C6FF00] text-[#0A0A0A] text-[9px] font-bold uppercase tracking-wider">
                    98% Match
                  </div>
                </div>
              </div>
            )}

            {label === "Match" && (
              <div className="flex-1 flex flex-col items-center justify-center text-center gap-3">
                <div className="w-20 h-20 rounded-full bg-[#C6FF00] flex items-center justify-center pulse-glow">
                  <Sparkles size={32} className="text-[#0A0A0A]" />
                </div>
                <div className="font-heading text-2xl text-white leading-tight">
                  IT'S A
                  <br />
                  <span className="text-[#C6FF00]">MATCH</span>
                </div>
                <div className="text-[10px] text-neutral-400 uppercase tracking-widest">
                  72h to lock it in
                </div>
              </div>
            )}

            {label === "Chat" && (
              <div className="flex-1 flex flex-col gap-2 justify-end">
                {[
                  { me: false, t: "Leg day at 6am 💪" },
                  { me: true, t: "Bet. Squats first?" },
                  { me: false, t: "Always. Don't be late." },
                ].map((m) => (
                  <div
                    key={m.t}
                    className={`max-w-[80%] px-3 py-2 rounded-2xl text-[11px] ${
                      m.me
                        ? "self-end bg-[#C6FF00] text-[#0A0A0A] font-semibold"
                        : "self-start bg-white/10 text-white"
                    }`}
                  >
                    {m.t}
                  </div>
                ))}
                <div className="mt-2 h-8 rounded-full bg-white/5 border border-white/10" />
              </div>
            )}

            {label === "Schedule" && (
              <div className="flex-1 flex flex-col gap-2">
                <div className="text-[10px] text-neutral-500 uppercase tracking-widest">
                  This week
                </div>
                {[
                  { d: "MON", t: "Push · 6:00 AM", on: true },
                  { d: "WED", t: "Pull · 7:00 PM", on: false },
                  { d: "FRI", t: "Legs · 6:00 AM", on: true },
                  { d: "SAT", t: "Cardio · 9:00 AM", on: false },
                ].map((s) => (
                  <div
                    key={s.d}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
                      s.on
                        ? "bg-[#C6FF00]/10 border-[#C6FF00]/40"
                        : "bg-white/5 border-white/10"
                    }`}
                  >
                    <span className="font-heading text-white text-sm">{s.d}</span>
                    <span className="text-[10px] text-neutral-300">{s.t}</span>
                    {s.on && <span className="w-1.5 h-1.5 rounded-full bg-[#C6FF00]" />}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 text-center">
        <div className="inline-flex items-center gap-2 mb-1">
          <Icon size={14} className="text-[#C6FF00]" />
          <span className="font-heading text-base text-white tracking-wide">{label}</span>
        </div>
        <p className="text-[11px] text-neutral-500 max-w-[220px] mx-auto">{accent}</p>
      </div>
    </div>
  );
};

const AppPreview = () => {
  return (
    <section
      id="app-preview"
      data-testid="app-preview-section"
      className="relative py-24 md:py-32 bg-[#0A0A0A] overflow-hidden"
    >
      {/* Glow */}
      <div className="absolute left-1/2 -translate-x-1/2 top-1/3 w-[800px] h-[400px] bg-[#C6FF00]/10 blur-[140px] rounded-full pointer-events-none" />

      <div className="relative container mx-auto max-w-7xl px-6 md:px-10">
        <div className="text-center mb-16 md:mb-20 max-w-3xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C6FF00] font-semibold mb-4">
            See It In Action
          </p>
          <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl text-white leading-[0.95]">
            Swipe. Match.
            <br />
            <span className="text-[#C6FF00]">Train. Repeat.</span>
          </h2>
          <p className="mt-6 text-base text-neutral-400 max-w-xl mx-auto">
            Designed for the way you actually train. Every screen is built to
            help you find a partner that shows up.
          </p>
        </div>

        <div className="flex gap-6 md:gap-10 overflow-x-auto no-scrollbar pb-8 md:justify-center md:flex-wrap md:overflow-visible">
          {screens.map((s, i) => (
            <PhoneMock key={s.label} index={i} label={s.label} Icon={s.icon} accent={s.accent} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default AppPreview;
